/**
 * Origen único de navegación del Workspace 0E3.
 * Consumido por NavRail, NavFlyout, NavMobileDrawer y Sidebar legacy mobile.
 * POS-UX-CONTROL-RECOVERY-001: labels en expandido, iconos diferenciados, Centro de Control.
 */
import {
  FaHome,
  FaBox,
  FaShoppingCart,
  FaShoppingBag,
  FaUsers,
  FaChartBar,
  FaTag,
  FaCog,
  FaBuilding,
  FaWarehouse,
  FaMoneyBillWave,
  FaClipboardCheck,
  FaUserShield,
  FaFlask,
  FaFileInvoiceDollar,
  FaCashRegister,
  FaBoxes,
  FaTags,
  FaTruck,
  FaStore
} from 'react-icons/fa';
import { isSuperAdminEmail } from './superAdmin';
import { matrixAllows, splitPermissionKey } from '../utils/matrixAllows';

/** @typedef {{ path?: string, icon: import('react').ComponentType, label: string, group?: string, permission?: string|null, moduleKey?: string, description?: string, highlight?: boolean, submenu?: Array<{ path: string, label: string, permission?: string, module?: string, adminOnly?: boolean, description?: string }> }} NavMenuItem */

export const NAV_GROUPS = [
  { id: 'control', label: 'Control' },
  { id: 'venta', label: 'Venta' },
  { id: 'catalogo', label: 'Catálogo' },
  { id: 'inventario', label: 'Inventario' },
  { id: 'personas', label: 'Personas' },
  { id: 'finanzas', label: 'Finanzas' },
  { id: 'analisis', label: 'Análisis' },
  { id: 'sistema', label: 'Sistema' }
];

/** Array canónico — no duplicar en otros componentes. */
export const NAV_MENU_ITEMS = [
  {
    path: '/',
    icon: FaHome,
    label: 'Dashboard',
    group: 'control',
    description: 'Resumen del negocio',
    permission: null
  },
  {
    path: '/centro-operaciones',
    icon: FaClipboardCheck,
    label: 'Centro de Control',
    group: 'control',
    description: 'Detectá problemas y resolvé lo importante',
    permission: null,
    highlight: true
  },
  {
    path: '/centro-comercial',
    icon: FaStore,
    label: 'Centro Comercial',
    group: 'control',
    description: 'Listas, promociones, combos y material para compartir',
    permission: null,
    highlight: true
  },
  {
    path: '/punto-venta',
    icon: FaCashRegister,
    label: 'Punto de Venta',
    group: 'venta',
    moduleKey: 'punto_venta',
    permission: 'ventas.crear',
    description: 'Cobrar y vender'
  },
  {
    icon: FaShoppingBag,
    label: 'Ventas',
    group: 'venta',
    permission: 'ventas',
    moduleKey: 'ventas',
    description: 'Historórico y devoluciones',
    submenu: [
      { path: '/ventas', label: 'Lista de Ventas', module: 'ventas', permission: 'ventas.ver' },
      { path: '/devoluciones', label: 'Devoluciones', module: 'devoluciones', permission: 'devoluciones.ver' },
      { path: '/ventas-eliminadas', label: 'Ventas Eliminadas', module: 'ventas', permission: 'ventas.ver', adminOnly: true }
    ]
  },
  {
    icon: FaBox,
    label: 'Productos',
    group: 'catalogo',
    permission: 'productos',
    description: 'Catálogo',
    submenu: [
      { path: '/productos', label: 'Lista de Productos', permission: 'productos.ver', description: 'Inventario de productos' },
      { path: '/productos/nuevo', label: 'Nuevo Producto', permission: 'productos.crear' },
      { path: '/categorias', label: 'Categorías', permission: 'categorias.ver' }
    ]
  },
  {
    path: '/combos',
    icon: FaBoxes,
    label: 'Combos',
    group: 'catalogo',
    permission: 'combos.ver',
    description: 'Paquetes comerciales'
  },
  {
    path: '/productos/precios',
    icon: FaTags,
    label: 'Listas de precios',
    group: 'catalogo',
    permission: 'listas_precios.ver',
    description: 'Administrar listas y exportaciones'
  },
  {
    icon: FaFlask,
    label: 'Producción',
    group: 'catalogo',
    permission: 'produccion',
    submenu: [
      { path: '/materias-primas', label: 'Materias Primas', permission: 'materias_primas.ver' },
      { path: '/recetas', label: 'Recetas', permission: 'recetas.ver' },
      { path: '/produccion', label: 'Órdenes de Producción', permission: 'produccion.ver' }
    ]
  },
  {
    icon: FaWarehouse,
    label: 'Inventario',
    group: 'inventario',
    permission: 'stock',
    submenu: [
      { path: '/stock', label: 'Stock por Sucursal', permission: 'stock.ver' },
      { path: '/stock/transferencias', label: 'Transferencias', permission: 'transferencias.ver' },
      { path: '/stock/ajustes', label: 'Ajustes de Stock', permission: 'stock.ajustar' },
      { path: '/stock/control', label: 'Control de Stock', permission: null }
    ]
  },
  {
    icon: FaShoppingCart,
    label: 'Compras',
    group: 'inventario',
    permission: 'compras',
    submenu: [
      { path: '/compras', label: 'Lista de Compras', permission: 'compras.ver' },
      { path: '/compras/nueva', label: 'Nueva Compra', permission: 'compras.crear' }
    ]
  },
  {
    icon: FaTruck,
    label: 'Proveedores',
    group: 'personas',
    permission: 'proveedores',
    moduleKey: 'proveedores',
    description: 'Proveedores y CxP',
    submenu: [
      { path: '/proveedores', label: 'Panel principal', permission: 'proveedores.ver' },
      { path: '/proveedores#listado', label: 'Listado', permission: 'proveedores.ver' },
      { path: '/proveedores/nuevo', label: 'Nuevo proveedor', permission: 'proveedores.crear' },
      { path: '/finanzas/cuentas-por-pagar', label: 'Cuentas por pagar', permission: 'proveedores.cuenta_corriente.ver' },
      { path: '/compras', label: 'Compras relacionadas', permission: 'compras.ver' }
    ]
  },
  {
    icon: FaUsers,
    label: 'Clientes',
    group: 'personas',
    permission: 'clientes',
    submenu: [
      { path: '/clientes', label: 'Lista de Clientes', permission: 'clientes.ver' },
      { path: '/clientes/nuevo', label: 'Nuevo Cliente', permission: 'clientes.crear' }
    ]
  },
  {
    path: '/cuenta-corriente',
    icon: FaFileInvoiceDollar,
    label: 'Cuenta Corriente',
    group: 'personas',
    permission: 'clientes',
    description: 'Cobros y saldos'
  },
  {
    icon: FaMoneyBillWave,
    label: 'Finanzas',
    group: 'finanzas',
    permission: 'caja',
    submenu: [
      { path: '/caja', label: 'Caja', module: 'caja', permission: 'caja.ver' },
      { path: '/finanzas/gastos', label: 'Gastos', module: 'caja', permission: 'caja.ver' },
      { path: '/finanzas/cuentas-por-pagar', label: 'Cuentas por pagar', permission: 'proveedores.cuenta_corriente.ver' }
    ]
  },
  {
    icon: FaTag,
    label: 'Promociones',
    group: 'finanzas',
    permission: 'promociones',
    submenu: [
      { path: '/promociones', label: 'Lista de Promociones', permission: 'promociones.ver' },
      { path: '/promociones/nueva', label: 'Nueva Promoción', permission: 'promociones.crear' }
    ]
  },
  {
    icon: FaChartBar,
    label: 'Reportes',
    group: 'analisis',
    permission: 'reportes',
    submenu: [
      { path: '/reportes/ventas', label: 'Reporte de Ventas', permission: 'reportes.ver' },
      { path: '/reportes/productos', label: 'Estadísticas de Ventas', permission: 'reportes.ver' },
      { path: '/reportes/ganancias', label: 'Reporte de Ganancias', permission: 'reportes.ver_ganancias' },
      { path: '/reportes/compras', label: 'Reporte de Compras', permission: 'reportes.ver' },
      { path: '/finanzas/gastos', label: 'Informe de Gastos', permission: 'reportes.ver' }
    ]
  },
  {
    icon: FaUserShield,
    label: 'Usuarios',
    group: 'sistema',
    permission: 'usuarios',
    submenu: [
      { path: '/usuarios', label: 'Lista de Usuarios', permission: 'usuarios.ver' },
      { path: '/usuarios/nuevo', label: 'Nuevo Usuario', permission: 'usuarios.crear' },
      { path: '/usuarios/permisos', label: 'Gestión de Permisos', permission: 'usuarios.editar' }
    ]
  },
  {
    icon: FaCog,
    label: 'Configuración',
    group: 'sistema',
    permission: 'configuracion',
    submenu: [
      { path: '/sucursales', label: 'Sucursales', permission: 'sucursales.ver' },
      { path: '/configuracion/empresa', label: 'Datos Empresa', permission: 'configuracion.ver' },
      { path: '/auditoria', label: 'Auditoría', permission: 'auditoria.ver' }
    ]
  }
];

const CONFIG_PATHS_SAFE = ['/sucursales', '/configuracion/empresa', '/auditoria'];

function moduleEnabled(companyModules, permOrModule) {
  if (!permOrModule) return true;
  const key = permOrModule.includes('.') ? permOrModule.split('.')[0] : permOrModule;
  return companyModules?.[key] !== false;
}

/**
 * Filtra menú según permisos, módulos y rol — misma lógica que Sidebar legacy.
 */
export function filterNavMenuItems({
  items = NAV_MENU_ITEMS,
  currentUser,
  hasPermission,
  companyModules,
  permisosEfectivos
}) {
  const superAdmin = isSuperAdminEmail(currentUser?.email);
  const checkPerm = (permission) => {
    if (!permission) return true;
    const parsed = splitPermissionKey(permission);
    if (!parsed) return true;
    const [mod, act] = parsed;
    if (permisosEfectivos && typeof permisosEfectivos === 'object') {
      return matrixAllows(permisosEfectivos, mod, act) === true;
    }
    return hasPermission(mod, act) === true;
  };

  return items
    .map((item) => {
      let submenu = item.submenu?.filter((sub) => sub.module !== 'wip');
      submenu = submenu?.filter((sub) => {
        if (sub.adminOnly && !superAdmin && currentUser?.rol !== 'admin' && currentUser?.rol !== 'administrador') {
          // adminOnly: keep for users with permission; filter only if no perm
        }
        const modKey = sub.module || (sub.permission ? sub.permission.split('.')[0] : null);
        if (superAdmin) return true;
        const modOk = moduleEnabled(companyModules, modKey);
        const permOk = checkPerm(sub.permission);
        const isConfigRutaSens = ['/admin', '/configuracion/modulos', '/configuracion/licencias'].includes(sub.path);
        if (isConfigRutaSens && !superAdmin) return false;
        return modOk && permOk;
      });
      if (submenu && submenu.length === 0) submenu = undefined;

      if (item.label === 'Configuración' && !superAdmin && submenu) {
        submenu = submenu.filter((s) => CONFIG_PATHS_SAFE.includes(s.path));
      }

      return { ...item, submenu };
    })
    .filter((item) => {
      if (superAdmin) return true;
      // Items con permission dotted (ej. combos.ver / ventas.crear)
      if (item.permission && item.permission.includes('.')) {
        const modKey = item.moduleKey || item.permission.split('.')[0];
        if (!moduleEnabled(companyModules, modKey)) return false;
        return checkPerm(item.permission);
      }
      const modOk = moduleEnabled(companyModules, item.moduleKey || item.permission);
      if (!modOk) return false;
      if (!item.permission) return true;
      if (item.submenu) return item.submenu.length > 0;
      return checkPerm(`${item.permission}.ver`) || hasPermission(item.permission, 'ver');
    });
}

export function isNavPathActive(pathname, path) {
  if (!path) return false;
  if (path === '/') return pathname === '/' || pathname === '/dashboard';
  return pathname === path || pathname.startsWith(`${path}/`);
}

export function isNavParentActive(pathname, submenu) {
  return submenu?.some((sub) => isNavPathActive(pathname, sub.path)) ?? false;
}

/** Comprueba si una ruta está en el menú ya filtrado por permisos. */
export function navMenuIncludesPath(menuItems, targetPath) {
  if (!targetPath || !menuItems?.length) return false;
  const base = targetPath.split('?')[0];
  for (const item of menuItems) {
    if (item.path === base) return true;
    if (item.submenu?.some((sub) => sub.path === base)) return true;
  }
  return false;
}

/** Calcula top del flyout alineado al ícono, con clamp al viewport. */
export function computeFlyoutTop(anchorTop, flyoutHeight, viewportHeight = window.innerHeight) {
  const margin = 12;
  const preferred = anchorTop - 4;
  const maxTop = Math.max(margin, viewportHeight - flyoutHeight - margin);
  return Math.max(margin, Math.min(preferred, maxTop));
}

/** Agrupa items filtrados para sidebar expandido. */
export function groupNavMenuItems(items) {
  const byGroup = new Map();
  for (const item of items || []) {
    const gid = item.group || 'otros';
    if (!byGroup.has(gid)) byGroup.set(gid, []);
    byGroup.get(gid).push(item);
  }
  const sections = [];
  for (const g of NAV_GROUPS) {
    const list = byGroup.get(g.id);
    if (list?.length) sections.push({ id: g.id, label: g.label, items: list });
  }
  for (const [gid, list] of byGroup) {
    if (!NAV_GROUPS.some((g) => g.id === gid) && list.length) {
      sections.push({ id: gid, label: gid, items: list });
    }
  }
  return sections;
}
